import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { Add, Edit, Delete, Save } from "@mui/icons-material";
import { workflowAPI } from "../../services/api";

const WorkflowSettings = ({ configurationId }) => {
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingColumn, setEditingColumn] = useState(null);
  const [formData, setFormData] = useState({
    column_name: "",
    display_name: "",
    is_active: true,
  });

  useEffect(() => {
    if (configurationId) {
      loadColumns();
    } else {
      setColumns([]);
    }
  }, [configurationId]);

  const loadColumns = async () => {
    if (!configurationId) {
      setColumns([]);
      return;
    }
    try {
      setError("");
      const response = await workflowAPI.getConfig(configurationId);
      setColumns(response.data || []);
    } catch (err) {
      console.error("Failed to load workflow columns:", err);
      setError(err.response?.data?.error || "Failed to load workflow columns.");
      setColumns([]);
    }
  };

  const handleOpenDialog = (column = null) => {
    if (column) {
      setEditingColumn(column);
      setFormData({
        column_name: column.column_name,
        display_name: column.display_name,
        is_active: column.is_active,
      });
    } else {
      setEditingColumn(null);
      setFormData({
        column_name: "",
        display_name: "",
        is_active: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingColumn(null);
    setFormData({
      column_name: "",
      display_name: "",
      is_active: true,
    });
  };

  const handleSave = async () => {
    if (!configurationId) {
      setError("Please select a configuration first");
      return;
    }

    if (!formData.column_name || !formData.display_name) {
      setError("Column name and display name are required");
      return;
    }

    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const updatedColumns = [...columns];

      if (editingColumn) {
        // Update existing column
        const index = updatedColumns.findIndex(
          (c) => c.id === editingColumn.id,
        );
        if (index >= 0) {
          updatedColumns[index] = { ...updatedColumns[index], ...formData };
        }
      } else {
        // Add new column
        const newColumn = {
          id: `temp-${Date.now()}`,
          column_name: formData.column_name,
          display_name: formData.display_name,
          column_order: updatedColumns.length + 1,
          is_active: formData.is_active,
        };
        updatedColumns.push(newColumn);
      }

      // Save to backend
      await workflowAPI.updateConfig({
        configuration_id: configurationId,
        columns: updatedColumns.map((col, index) => ({
          column_name: col.column_name,
          display_name: col.display_name,
          column_order: index + 1,
          is_active: col.is_active,
        })),
      });

      setSuccess(
        editingColumn
          ? "Column updated successfully!"
          : "Column added successfully!",
      );
      handleCloseDialog();
      loadColumns();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save column");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (columnId) => {
    if (!window.confirm("Are you sure you want to delete this column?")) {
      return;
    }

    try {
      const updatedColumns = columns.filter((c) => c.id !== columnId);

      await workflowAPI.updateConfig({
        configuration_id: configurationId,
        columns: updatedColumns.map((col, index) => ({
          column_name: col.column_name,
          display_name: col.display_name,
          column_order: index + 1,
          is_active: col.is_active,
        })),
      });

      setSuccess("Column deleted successfully!");
      loadColumns();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete column");
    }
  };

  const handleToggleActive = async (column) => {
    try {
      const updatedColumns = columns.map((c) =>
        c.id === column.id ? { ...c, is_active: !c.is_active } : c,
      );

      await workflowAPI.updateConfig({
        configuration_id: configurationId,
        columns: updatedColumns.map((col, index) => ({
          column_name: col.column_name,
          display_name: col.display_name,
          column_order: index + 1,
          is_active: col.is_active,
        })),
      });

      loadColumns();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update column");
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Workflow Board Configuration
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure the column names and order for the workflow board. Columns
        define the stages of your change request workflow.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="subtitle1">Workflow Columns</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          disabled={!configurationId}
        >
          Add Column
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Order</TableCell>
              <TableCell>Column Name</TableCell>
              <TableCell>Display Name</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {columns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="text.secondary">
                    No columns configured. Add a column to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              columns
                .sort((a, b) => a.column_order - b.column_order)
                .map((column) => (
                  <TableRow key={column.id}>
                    <TableCell>{column.column_order}</TableCell>
                    <TableCell>{column.column_name}</TableCell>
                    <TableCell>{column.display_name}</TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={column.is_active}
                            onChange={() => handleToggleActive(column)}
                            size="small"
                          />
                        }
                        label={column.is_active ? "Active" : "Inactive"}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(column)}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(column.id)}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingColumn ? "Edit Column" : "Add Column"}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Column Name"
            variant="outlined"
            margin="normal"
            value={formData.column_name}
            onChange={(e) =>
              setFormData({ ...formData, column_name: e.target.value })
            }
            required
            placeholder="e.g., Owner, Engineering Lead"
            helperText="Internal identifier for the column (used in database)"
            disabled={!!editingColumn}
          />
          <TextField
            fullWidth
            label="Display Name"
            variant="outlined"
            margin="normal"
            value={formData.display_name}
            onChange={(e) =>
              setFormData({ ...formData, display_name: e.target.value })
            }
            required
            placeholder="e.g., Owner, Engineering Lead, Engineer"
            helperText="Name shown on the workflow board"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={(e) =>
                  setFormData({ ...formData, is_active: e.target.checked })
                }
              />
            }
            label="Active"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {loading ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default WorkflowSettings;
